import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { buildAuthHref, rememberAuthIntent } from '@/features/auth/auth-intent';
import { useAuthStore } from '@/features/auth/store';
import { buildClubHref } from '@/features/catalog/catalog.api';
import { fetchReaderBootstrap } from '@/features/reader/reader.api';
import { deriveReaderContentItems, type ReaderContentItem } from '@/features/reader/deriveReaderContentItems';
import { ReaderImageGallery } from '@/features/reader/ReaderImageGallery';
import { ReaderRoundVideoSequence } from '@/features/reader/ReaderRoundVideoSequence';
import { useProgressTracker } from '@/features/reader/useProgressTracker';
import { useSingleActiveMediaCoordinator } from '@/features/reader/useSingleActiveMediaCoordinator';
import { BlockRenderer } from '@/shared/components/blocks/BlockRenderer';
import { normalizeRichTextHtml } from '@/shared/components/blocks/MediaCaption';
import { ReaderChrome } from '@/shared/components/reader/ReaderChrome';
import { ReaderToc } from '@/shared/components/reader/ReaderToc';
import { NeutralLoadingState } from '@/shared/components/ui/NeutralLoadingState';
import { ErrorState } from '@/shared/components/ui/ErrorState';
import { PrimaryButton } from '@/shared/components/ui/PrimaryButton';
import { useTelegramBackButton } from '@/telegram';
import { usePullToRefreshSurface } from '@/pwa/PullToRefreshProvider';
import { trackEvent } from '@/shared/lib/analytics';
import { trackYandexGoal } from '@/shared/lib/yandex-metrica';
import { VIDEO_COMPLETE_THRESHOLD, type ReaderBlock, type ReaderChapterMeta, type ReaderMediaProgressItem } from '@bookclub/shared';

const EMBED_BLOCK_TYPES = new Set<ReaderBlock['type']>(['image', 'video', 'audio']);

function getInterBlockSpacerClass(currentBlock: ReaderBlock, nextBlock: ReaderBlock) {
  if (nextBlock.type === 'text' && EMBED_BLOCK_TYPES.has(currentBlock.type)) return 'h-5';
  if (EMBED_BLOCK_TYPES.has(nextBlock.type) && currentBlock.type === 'text') return 'h-6';
  if (EMBED_BLOCK_TYPES.has(nextBlock.type) && EMBED_BLOCK_TYPES.has(currentBlock.type)) return 'h-4';
  if (nextBlock.type === 'heading' && currentBlock.type === 'text') return 'h-6';
  if (nextBlock.type === 'quote' || nextBlock.type === 'callout' || nextBlock.type === 'list' || nextBlock.type === 'external_link')
    return 'h-4';
  if (nextBlock.type === 'divider') return 'h-6';
  return 'h-4';
}

function shouldRenderBlockSeparator(currentBlock: ReaderBlock, nextBlock: ReaderBlock) {
  return currentBlock.type !== 'divider' && nextBlock.type !== 'divider';
}

function isTelegramVoiceAudioBlock(block: ReaderBlock) {
  return block.type === 'audio' && (block.payload['style'] === 'voice' || block.payload['telegramSourceType'] === 'voice');
}

function hasVisibleBlockContent(block: ReaderBlock) {
  const p = block.payload;

  switch (block.type) {
    case 'text':
      return normalizeRichTextHtml((p['text'] as string | undefined) ?? '').length > 0;
    case 'heading':
      return typeof p['text'] === 'string' && p['text'].trim().length > 0;
    case 'quote':
      return (
        (typeof p['text'] === 'string' && p['text'].trim().length > 0) ||
        (typeof p['author'] === 'string' && p['author'].trim().length > 0)
      );
    case 'list':
      return Array.isArray(p['items']) && p['items'].some((item) => typeof item === 'string' && item.trim().length > 0);
    case 'image':
      return Boolean(block.mediaUrl || (typeof p['url'] === 'string' && p['url'].trim().length > 0));
    case 'audio':
      return isTelegramVoiceAudioBlock(block) || Boolean(block.mediaUrl || (typeof p['url'] === 'string' && p['url'].trim().length > 0));
    case 'video':
      return true;
    case 'divider':
      return true;
    case 'callout':
      return (
        (typeof p['text'] === 'string' && p['text'].trim().length > 0) ||
        (typeof p['emoji'] === 'string' && p['emoji'].trim().length > 0)
      );
    case 'external_link':
      return typeof p['url'] === 'string' && p['url'].startsWith('https://');
    default:
      return true;
  }
}

function getContentItemBoundaryBlocks(item: ReaderContentItem) {
  if (item.kind === 'block') {
    if (!hasVisibleBlockContent(item.block)) return null;
    return {
      firstBlock: item.block,
      lastBlock: item.block,
    };
  }

  const visibleBlocks = item.blocks.filter(hasVisibleBlockContent);
  if (visibleBlocks.length === 0) return null;

  return {
    firstBlock: visibleBlocks[0],
    lastBlock: visibleBlocks[visibleBlocks.length - 1],
  };
}

function InterBlockSpacer({
  currentBlock,
  nextBlock,
}: {
  currentBlock: ReaderBlock;
  nextBlock: ReaderBlock;
}) {
  const showSeparator = shouldRenderBlockSeparator(currentBlock, nextBlock);

  return (
    <div
      aria-hidden="true"
      data-testid={`reader-spacer-${currentBlock.id}-${nextBlock.id}`}
      className={[
        getInterBlockSpacerClass(currentBlock, nextBlock),
        showSeparator ? 'flex items-center justify-center' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showSeparator ? (
        <div
          data-testid={`reader-block-separator-${currentBlock.id}-${nextBlock.id}`}
          className="h-px w-10 rounded-full bg-[rgba(114,86,50,0.18)] shadow-[0_0_0_1px_rgba(255,255,255,0.42)]"
        />
      ) : null}
    </div>
  );
}

export function ReaderPage() {
  const { clubId, chapterId } = useParams<{ clubId: string; chapterId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const runtime = useAuthStore((s) => s.runtime);
  const clubSource = useMemo(
    () => (new URLSearchParams(location.search).get('source') === 'my-clubs' ? 'my-clubs' : 'catalog'),
    [location.search],
  );
  const sourceSearch = useMemo(() => `?source=${clubSource}`, [clubSource]);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showToc, setShowToc] = useState(false);
  const [mediaProgressByBlockId, setMediaProgressByBlockId] = useState<Record<string, ReaderMediaProgressItem>>({});

  const scrollRef = useRef<HTMLDivElement>(null);
  const resumeTimeoutsRef = useRef<number[]>([]);
  const openedRoundVideoSequencesRef = useRef<Set<string>>(new Set());

  usePullToRefreshSurface('reader', scrollRef);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reader', clubId, chapterId],
    queryFn: () => fetchReaderBootstrap(clubId!, chapterId!),
    enabled: !!clubId && !!chapterId,
    staleTime: 0,
    retry: false,
  });

  const { trackScroll, trackVideoProgress, trackVideoEnded } = useProgressTracker({
    chapterId: chapterId!,
    enabled: !!data && !isLoading && isAuthenticated,
  });
  const paywallReference = data?.club.paywallSlug ?? clubId;
  const clubHref = useMemo(
    () => (data ? buildClubHref(data.club, clubSource) : `/clubs/${clubId}?source=${clubSource}`),
    [clubId, clubSource, data],
  );
  const isDemoReader = data?.access === 'demo' || data?.chapter.isDemo === true;
  const demoContext = isDemoReader ? 'demo_reader' : null;
  const paywallHref = useMemo(
    () =>
      `/paywall/${paywallReference}?${new URLSearchParams({
        source: clubSource,
        ...(demoContext ? { demo: 'reader' } : {}),
      }).toString()}`,
    [clubSource, demoContext, paywallReference],
  );
  const { registerMediaElement, handleMediaPlay, handleMediaStop } = useSingleActiveMediaCoordinator();

  const handleBack = useCallback(() => {
    navigate(clubHref, { replace: true });
  }, [clubHref, navigate]);

  useTelegramBackButton(handleBack);

  useEffect(() => {
    if (clubId && chapterId) {
      trackEvent('read_open', { clubId, chapterId });
    }
  }, [clubId, chapterId]);

  useEffect(() => {
    if (!clubId || !chapterId || !demoContext) {
      return;
    }

    trackYandexGoal(
      'demo_read_open',
      {
        club_id: clubId,
        club_slug: data?.club.paywallSlug ?? undefined,
        chapter_id: chapterId,
        source_surface: 'reader',
        demo_context: demoContext,
      },
      {
        dedupeKey: `${clubId}:${chapterId}`,
      },
    );
  }, [chapterId, clubId, data?.club.paywallSlug, demoContext]);

  const handleScroll = useCallback(() => {
    trackScroll(scrollRef.current);
  }, [trackScroll]);

  useEffect(() => {
    const container = scrollRef.current;
    const resumePosition = data?.progressSnapshot?.lastPosition;

    if (!container || resumePosition === null || resumePosition === undefined || resumePosition <= 0) {
      return;
    }

    let cancelled = false;

    for (const timeoutId of resumeTimeoutsRef.current) {
      window.clearTimeout(timeoutId);
    }
    resumeTimeoutsRef.current = [];

    const applyResumePosition = () => {
      if (cancelled) return;
      const scrollableDistance = container.scrollHeight - container.clientHeight;
      if (scrollableDistance <= 0) return;
      container.scrollTop = Math.round(scrollableDistance * resumePosition);
    };

    const scheduleResume = (delayMs: number) => {
      const timeoutId = window.setTimeout(applyResumePosition, delayMs);
      resumeTimeoutsRef.current.push(timeoutId);
    };

    scheduleResume(0);
    scheduleResume(120);
    scheduleResume(360);
    scheduleResume(900);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => applyResumePosition());
      resizeObserver.observe(container);
    }

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      for (const timeoutId of resumeTimeoutsRef.current) {
        window.clearTimeout(timeoutId);
      }
      resumeTimeoutsRef.current = [];
    };
  }, [data?.chapter.id, data?.progressSnapshot?.lastPosition]);

  useEffect(() => {
    if (!data) {
      setMediaProgressByBlockId({});
      openedRoundVideoSequencesRef.current.clear();
      return;
    }

    setMediaProgressByBlockId(
      Object.fromEntries(data.mediaProgress.map((progress) => [progress.blockId, progress])),
    );
  }, [data]);

  const handleToggleOverlay = useCallback(() => {
    setShowOverlay((v) => {
      const next = !v;
      trackEvent('reader_controls_toggle', { state: next ? 'shown' : 'hidden' });
      return next;
    });
  }, []);

  const handleNavigateChapter = useCallback(
    (direction: 'prev' | 'next') => {
      if (!data) return;
      const sorted = [...data.chapters].sort((a, b) => a.index - b.index);
      const idx = sorted.findIndex((c) => c.id === chapterId);
      const target = direction === 'prev' ? sorted[idx - 1] : sorted[idx + 1];
      if (!target) return;

      if (target.access === 'locked') {
        if (demoContext) {
          trackYandexGoal('demo_purchase_click', {
            club_id: clubId!,
            club_slug: data.club.paywallSlug ?? undefined,
            chapter_id: target.id,
            source_surface: 'reader_next_prev',
            demo_context: demoContext,
          });
        }
        if (runtime === 'web' && !isAuthenticated) {
          const returnTo = paywallHref;
          rememberAuthIntent(returnTo);
          navigate(buildAuthHref(returnTo));
          return;
        }
        trackEvent('locked_chapter_attempt', {
          clubId,
          chapterId: target.id,
          source: 'next_prev',
        });
        navigate(paywallHref);
        return;
      }

      trackEvent(direction === 'prev' ? 'chapter_prev' : 'chapter_next', {
        clubId,
        chapterId: target.id,
      });
      navigate(`/reader/${clubId}/${target.id}${sourceSearch}`);
    },
    [data, chapterId, clubId, demoContext, isAuthenticated, navigate, paywallHref, runtime, sourceSearch],
  );

  const handleTocChapterTap = useCallback(
    (chapter: ReaderChapterMeta) => {
      if (chapter.access === 'locked') {
        if (demoContext) {
          trackYandexGoal('demo_purchase_click', {
            club_id: clubId!,
            club_slug: data?.club.paywallSlug ?? undefined,
            chapter_id: chapter.id,
            source_surface: 'reader_toc',
            demo_context: demoContext,
          });
        }
        if (runtime === 'web' && !isAuthenticated) {
          const returnTo = paywallHref;
          rememberAuthIntent(returnTo);
          navigate(buildAuthHref(returnTo));
          return;
        }
        trackEvent('locked_chapter_attempt', {
          clubId,
          chapterId: chapter.id,
          source: 'toc',
        });
        navigate(paywallHref);
        return;
      }
      trackEvent('toc_chapter_select', { clubId, chapterId: chapter.id });
      setShowToc(false);
      navigate(`/reader/${clubId}/${chapter.id}${sourceSearch}`);
    },
    [clubId, data?.club.paywallSlug, demoContext, isAuthenticated, navigate, paywallHref, runtime, sourceSearch],
  );

  const handleVideoTimeUpdate = useCallback(
    (blockId: string, positionSeconds: number, durationSeconds: number) => {
      trackVideoProgress(blockId, positionSeconds, durationSeconds);

      setMediaProgressByBlockId((current) => {
        const previous = current[blockId];
        const persisted = data?.mediaProgress.find((progress) => progress.blockId === blockId);
        const completedBaseline =
          previous?.state === 'completed'
            ? previous
            : persisted?.state === 'completed'
              ? persisted
              : null;
        const roundedPosition = Math.round(positionSeconds);
        const mediaCompleted =
          durationSeconds > 0 && positionSeconds / durationSeconds >= VIDEO_COMPLETE_THRESHOLD;

        if (completedBaseline && !mediaCompleted) {
          return {
            ...current,
            [blockId]: {
              ...completedBaseline,
              state: 'completed',
              positionSeconds: Math.max(completedBaseline.positionSeconds, roundedPosition),
            },
          };
        }

        return {
          ...current,
          [blockId]: {
            blockId,
            state: mediaCompleted ? 'completed' : roundedPosition > 0 ? 'in_progress' : 'not_started',
            positionSeconds: roundedPosition,
            manualCompleted: previous?.manualCompleted ?? false,
          },
        };
      });
    },
    [data?.mediaProgress, trackVideoProgress],
  );

  const handleVideoEnded = useCallback(
    (blockId: string, positionSeconds: number, durationSeconds: number) => {
      trackVideoEnded(blockId, positionSeconds, durationSeconds);

      setMediaProgressByBlockId((current) => ({
        ...current,
        [blockId]: {
          blockId,
          state: 'completed',
          positionSeconds: Math.round(durationSeconds || positionSeconds),
          manualCompleted: current[blockId]?.manualCompleted ?? false,
        },
      }));
    },
    [trackVideoEnded],
  );

  const handleRoundVideoSequenceOpen = useCallback(
    (payload: { clubId: string; chapterId: string; sequenceId: string; totalCount: number; viewedCount: number }) => {
      if (openedRoundVideoSequencesRef.current.has(payload.sequenceId)) {
        return;
      }

      openedRoundVideoSequencesRef.current.add(payload.sequenceId);
      trackEvent('round_video_sequence_open', payload);
    },
    [],
  );

  const handleRoundVideoTranscriptOpen = useCallback(
    (payload: { clubId: string; chapterId: string; sequenceId: string; blockId: string }) => {
      trackEvent('round_video_transcript_open', payload);
    },
    [],
  );

  const handleImageGallerySwipe = useCallback(
    (payload: {
      clubId: string;
      chapterId: string;
      galleryId: string;
      blockId: string;
      index: number;
      totalCount: number;
    }) => {
      trackEvent('image_gallery_swipe', payload);
    },
    [],
  );

  const handleImageFullscreenOpen = useCallback(
    (payload: {
      clubId: string;
      chapterId: string;
      galleryId: string;
      blockId: string;
      index: number;
      totalCount: number;
    }) => {
      trackEvent('image_fullscreen_open', payload);
    },
    [],
  );

  // ─── Loading state ──────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[var(--reader-bg)]">
        <NeutralLoadingState rows={3} ariaLabel="Загрузка главы" className="w-full max-w-xs px-6" />
      </div>
    );
  }

  // ─── Error states ───────────────────────────────────────────────────────────

  if (isError) {
    const err = error as { response?: { json: () => Promise<{ error?: { code?: string } }> } };
    const isLocked = err?.response !== undefined;

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[var(--reader-bg)] px-6">
        {isLocked ? (
          <ErrorState
            title="Глава закрыта"
            description="Для доступа к этой главе необходима подписка."
            icon={
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            }
            retryLabel="Получить доступ"
            onRetry={() => void navigate(paywallHref)}
            secondaryAction={
              <PrimaryButton tone="ghost" onClick={handleBack}>
                Назад к клубу
              </PrimaryButton>
            }
          />
        ) : (
          <ErrorState
            title="Ошибка загрузки"
            onRetry={handleBack}
            retryLabel="Назад"
          />
        )}
      </div>
    );
  }

  if (!data) return null;

  const sortedChapters = [...data.chapters].sort((a, b) => a.index - b.index);
  const currentIdx = sortedChapters.findIndex((c) => c.id === chapterId);
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < sortedChapters.length - 1;
  const nextChapter = hasNext ? sortedChapters[currentIdx + 1] : null;
  const prevChapter = hasPrev ? sortedChapters[currentIdx - 1] : null;
  const chapterBlocks = data.chapter.blocks;
  const readerContentItems = deriveReaderContentItems(chapterBlocks);
  const visibleReaderContentItems = readerContentItems
    .map((item) => ({
      item,
      boundary: getContentItemBoundaryBlocks(item),
    }))
    .filter((entry): entry is { item: ReaderContentItem; boundary: { firstBlock: ReaderBlock; lastBlock: ReaderBlock } } =>
      entry.boundary !== null,
    );
  const mediaProgressMap = new Map([
    ...data.mediaProgress.map((mp) => [mp.blockId, mp] as const),
    ...Object.values(mediaProgressByBlockId).map((mp) => [mp.blockId, mp] as const),
  ]);

  // ─── Reader UI ──────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-[linear-gradient(180deg,#efe4d1_0%,#f7f1e6_18%,#f5efe3_100%)]">
      <div
        ref={scrollRef}
        data-testid="reader-scroll-surface"
        data-pull-to-refresh-surface="reader"
        className="min-h-0 flex-1 overflow-y-auto overscroll-none"
        onScroll={handleScroll}
        onClick={handleToggleOverlay}
      >
        {showOverlay && <div className="h-[3.75rem] min-[430px]:h-[4.25rem]" />}

        <div
          data-testid="reader-content-frame"
          className="mx-auto w-full max-w-[var(--screen-max-width)] px-2 pb-[calc(var(--content-safe-area-bottom)+5.375rem)] pt-[calc(var(--content-safe-area-top)+0.25rem)] min-[430px]:px-3 min-[768px]:max-w-[var(--reader-media-max-width)] min-[768px]:px-[var(--desktop-gutter)]"
        >
          <div className="overflow-hidden rounded-[34px] border border-white/72 bg-[rgba(255,253,248,0.97)] shadow-[0_24px_42px_rgba(62,48,23,0.12)]">
            <div
              data-testid="reader-readable-column"
              className="mx-auto flex w-full max-w-[34rem] flex-col px-4 pb-7 pt-5 min-[430px]:px-5 min-[430px]:pb-8 min-[430px]:pt-[1.375rem] min-[768px]:max-w-[var(--reader-content-max-width)]"
            >
              <div className="px-1 text-center">
                {data.chapter.subtitle ? (
                  <p className="text-[11px] font-medium leading-[1.2] tracking-[0.01em] text-[rgba(26,26,26,0.42)]">
                    {data.chapter.subtitle}
                  </p>
                ) : null}
                <h1 className={['font-serif text-[25px] font-semibold leading-[1.06] tracking-[-0.03em] text-[rgba(58,49,39,0.94)] min-[430px]:text-[27px]', data.chapter.subtitle ? 'mt-1.5' : 'mt-0'].join(' ')}>
                  {data.chapter.title}
                </h1>
                <div className="mx-auto mt-3.5 h-px w-16 bg-[rgba(26,26,26,0.12)]" />
              </div>

              <div className="mt-5 space-y-0 min-[430px]:mt-6">
                {visibleReaderContentItems.map(({ item, boundary }, visibleIndex) => {
                  const nextBoundary = visibleReaderContentItems[visibleIndex + 1]?.boundary;

                  if (item.kind === 'block') {
                    const block = item.block;
                    const mp = mediaProgressMap.get(block.id);

                    return (
                      <div key={block.id}>
                        <BlockRenderer
                          block={block}
                          defaultVideoPoster={data.club.coverUrl}
                          mediaState={mp?.state}
                          registerMediaElement={registerMediaElement}
                          onMediaPlay={handleMediaPlay}
                          onMediaPause={handleMediaStop}
                          videoPosition={mp?.state === 'in_progress' ? mp.positionSeconds : undefined}
                          onVideoTimeUpdate={handleVideoTimeUpdate}
                          onVideoEnded={handleVideoEnded}
                        />
                        {nextBoundary ? (
                          <InterBlockSpacer
                            currentBlock={boundary.lastBlock}
                            nextBlock={nextBoundary.firstBlock}
                          />
                        ) : null}
                      </div>
                    );
                  }

                  if (item.kind === 'round_video_sequence') {
                    return (
                      <div key={item.id}>
                        <ReaderRoundVideoSequence
                          sequenceId={item.id}
                          blocks={item.blocks}
                          clubId={clubId!}
                          chapterId={chapterId!}
                          defaultVideoPoster={data.club.coverUrl}
                          mediaProgressMap={mediaProgressMap}
                          registerMediaElement={registerMediaElement}
                          onMediaPlay={handleMediaPlay}
                          onMediaPause={handleMediaStop}
                          onVideoTimeUpdate={handleVideoTimeUpdate}
                          onVideoEnded={handleVideoEnded}
                          onOpen={handleRoundVideoSequenceOpen}
                          onTranscriptOpen={handleRoundVideoTranscriptOpen}
                        />
                        {nextBoundary ? (
                          <InterBlockSpacer
                            currentBlock={boundary.lastBlock}
                            nextBlock={nextBoundary.firstBlock}
                          />
                        ) : null}
                      </div>
                    );
                  }

                  if (item.kind === 'image_gallery') {
                    return (
                      <div key={item.id}>
                        <ReaderImageGallery
                          galleryId={item.id}
                          blocks={item.blocks}
                          clubId={clubId!}
                          chapterId={chapterId!}
                          onImageChange={handleImageGallerySwipe}
                          onImageOpen={handleImageFullscreenOpen}
                        />
                        {nextBoundary ? (
                          <InterBlockSpacer
                            currentBlock={boundary.lastBlock}
                            nextBlock={nextBoundary.firstBlock}
                          />
                        ) : null}
                      </div>
                    );
                  }

                  return null;
                })}
              </div>

              {hasNext ? <div className="h-3" /> : null}
            </div>
          </div>
        </div>
      </div>

      <ReaderChrome
        visible={showOverlay}
        clubTitle={data.club.title}
        chapterTitle={data.chapter.title}
        onBack={handleBack}
        onOpenToc={() => {
          trackEvent('toc_open', { clubId, chapterId });
          setShowToc(true);
        }}
        previousLabel={prevChapter?.title ?? null}
        nextLabel={nextChapter?.title ?? null}
        nextLocked={nextChapter?.access === 'locked'}
        canGoPrevious={hasPrev}
        canGoNext={hasNext}
        onPrevious={() => handleNavigateChapter('prev')}
        onNext={() => handleNavigateChapter('next')}
      />

      {showToc && (
        <div className="fixed inset-0 z-50" onClick={() => setShowToc(false)}>
          <div className="absolute inset-0 bg-[rgba(22,16,8,0.36)]" />
          <div
            className="absolute inset-x-0 bottom-0 flex max-h-[72vh] flex-col rounded-t-[32px] border border-white/70 bg-[linear-gradient(180deg,rgba(255,251,244,0.98),rgba(247,240,227,0.96))] p-3 shadow-[0_-18px_36px_rgba(41,30,14,0.16)]"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 8px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-2 py-1">
              <div className="h-1.5 w-12 rounded-full bg-[rgba(26,26,26,0.12)]" />
              <button
                onClick={() => setShowToc(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/88 text-[rgba(26,26,26,0.38)]"
                aria-label="Закрыть оглавление"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-1">
              <ReaderToc
                chapters={sortedChapters}
                currentChapterId={chapterId!}
                onSelect={handleTocChapterTap}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
